<template>
  <div class="selection-shell" :style="hubThemeStyle">
    <v-container class="hub-container">
      <header class="hub-header text-center">
        <span class="hub-eyebrow">MEIN STUDIO</span>
        <h1 class="headline mb-3">Deine Buchungen</h1>
        <p class="sub-headline mb-0">{{ pageSubtitle }}</p>
      </header>

      <section class="reservations-view">
        <section class="upcoming-section bookings-overview" aria-labelledby="customer-bookings-title">
          <div class="section-title-row">
            <div>
              <h2 id="customer-bookings-title" class="section-headline">Als Nächstes</h2>
              <p class="section-help">Deine kommenden Termine und Reservierungen.</p>
            </div>
          </div>

          <div v-if="appointmentsError" class="appointments-notice" role="status">
            <v-icon small>mdi-calendar-alert-outline</v-icon>
            <span>Deine Trainertermine konnten gerade nicht geladen werden.</span>
            <button type="button" @click="fetchMyCalendarAppointments">Erneut versuchen</button>
          </div>

          <div v-if="isLoadingReservations || isLoadingAppointments" class="text-center hub-loading hub-loading--bookings">
            <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
          </div>

          <div v-else-if="upcomingBookingItems.length" class="bookings-list">
            <v-card
              v-for="item in upcomingBookingItems"
              :key="item.key"
              class="booking-card"
              :class="{
                'booking-card--appointment': item.kind === 'appointment',
                'booking-card--muted':
                  (item.kind === 'reservation' && item.reservation.isCancelled) ||
                  (item.kind === 'appointment' && isInactiveAppointment(item.appointment)),
              }">
              <div class="booking-card-icon">
                <v-icon :color="item.kind === 'reservation' && item.reservation.isCancelled ? 'red' : 'primary'">
                  {{
                    item.kind === "appointment"
                      ? "mdi-account-clock-outline"
                      : item.reservation.isCancelled
                      ? "mdi-calendar-remove-outline"
                      : "mdi-calendar-check-outline"
                  }}
                </v-icon>
              </div>

              <div v-if="item.kind === 'reservation'" class="booking-card-copy">
                <span class="booking-card-kind">Reservierung</span>
                <strong>{{ item.reservation.reservationType.name }}</strong>
                <span class="booking-card-time">{{ formatDateTime(item.reservation.startTime) }}</span>
                <v-chip v-if="item.reservation.isCancelled" x-small color="red" class="booking-state-chip"> Storniert (Defekt) </v-chip>
              </div>

              <div v-else class="booking-card-copy">
                <span class="booking-card-kind">Trainertermin</span>
                <strong>{{ item.appointment.title }}</strong>
                <span class="booking-card-time">
                  {{ appointmentWeekdayLong(item.appointment) }}, {{ formatAppointmentDate(item.appointment) }} ·
                  {{ appointmentTime(item.appointment) }}
                </span>
                <span class="booking-card-meta">
                  <span><v-icon x-small>mdi-account-outline</v-icon>{{ trainerName(item.appointment) }}</span>
                  <span><v-icon x-small>mdi-map-marker-outline</v-icon>{{ appointmentLocation(item.appointment) }}</span>
                </span>
              </div>

              <div v-if="item.kind === 'reservation' && !item.reservation.isCancelled" class="booking-card-action">
                <v-btn icon class="cancel-btn" title="Reservierung stornieren" @click.stop="promptCancelReservation(item.reservation)">
                  <v-icon>mdi-trash-can-outline</v-icon>
                </v-btn>
              </div>
            </v-card>
          </div>

          <div v-else class="no-reservations-placeholder no-reservations-placeholder--compact">
            <v-icon class="placeholder-icon">mdi-calendar-check-outline</v-icon>
            <p>Du hast aktuell keine kommenden Buchungen.</p>
          </div>
        </section>

        <section v-if="!isLoadingAppointments && pastTrainerAppointments.length" class="appointment-history">
          <button
            type="button"
            class="history-toggle"
            :aria-expanded="showPastAppointments ? 'true' : 'false'"
            aria-controls="past-trainer-appointments"
            @click="showPastAppointments = !showPastAppointments">
            <span class="history-toggle-icon"><v-icon>mdi-history</v-icon></span>
            <span class="history-toggle-copy">
              <strong>Vergangene Termine</strong>
              <small>Bewertungen ansehen</small>
            </span>
            <span class="history-toggle-count">{{ pastTrainerAppointments.length }}</span>
            <v-icon class="history-toggle-chevron" :class="{ 'history-toggle-chevron--open': showPastAppointments }"> mdi-chevron-down </v-icon>
          </button>

          <div v-show="showPastAppointments" id="past-trainer-appointments" class="appointment-history-panel">
            <div class="trainer-appointment-list">
              <article
                v-for="appointment in pastTrainerAppointments"
                :key="appointment._id"
                class="trainer-appointment-card"
                :class="{ 'trainer-appointment-card--muted': isInactiveAppointment(appointment) }">
                <div class="compact-date">
                  <strong>{{ appointmentDay(appointment) }}</strong>
                  <span>{{ appointmentMonth(appointment) }}</span>
                </div>
                <div class="appointment-card-copy">
                  <div class="appointment-card-title-row">
                    <h3>{{ appointment.title }}</h3>
                  </div>
                  <p class="appointment-card-time">
                    {{ appointmentWeekdayLong(appointment) }}, {{ formatAppointmentDate(appointment) }} ·
                    {{ appointmentTime(appointment) }}
                  </p>
                  <div class="appointment-card-facts">
                    <span><v-icon x-small>mdi-account-outline</v-icon>{{ trainerName(appointment) }}</span>
                    <span><v-icon x-small>mdi-map-marker-outline</v-icon>{{ appointmentLocation(appointment) }}</span>
                  </div>
                </div>
                <div class="appointment-card-action">
                  <template v-if="appointment.rating">
                    <div class="rating-summary">
                      <v-rating
                        :value="appointment.rating.stars"
                        readonly
                        dense
                        small
                        color="amber"
                        background-color="rgba(255,255,255,.18)"></v-rating>
                      <button type="button" @click="openRatingDialog(appointment)">Bewertung ändern</button>
                    </div>
                  </template>
                  <v-btn v-else-if="appointment.canRate" rounded outlined color="primary" class="text-none" @click="openRatingDialog(appointment)">
                    <v-icon left small>mdi-star-outline</v-icon>
                    Bewerten
                  </v-btn>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section v-if="isLoadingTypes || hasReservationOptions" class="booking-options-section" aria-labelledby="booking-options-title">
          <div class="section-divider section-divider--booking">
            <h2 id="booking-options-title" class="section-headline">Neu reservieren</h2>
            <p class="section-help">Wähle ein Angebot und danach eine freie Zeit.</p>
          </div>

          <div v-if="isLoadingTypes" class="text-center hub-loading hub-loading--compact">
            <v-progress-circular indeterminate color="primary" size="52"></v-progress-circular>
          </div>

          <div v-else class="type-feed">
            <v-card v-for="type in reservationTypes" :key="type._id" class="type-card" @click="selectType(type)">
              <div class="card-content">
                <div class="card-header">
                  <span class="type-icon"><v-icon>mdi-spa-outline</v-icon></span>
                  <v-chip small class="duration-chip" color="rgba(255,255,255,0.08)">
                    <v-icon left small>mdi-clock-outline</v-icon>
                    {{ type.timeslotDuration }} min
                  </v-chip>
                </div>
                <h2 class="card-title">{{ type.name }}</h2>
                <p class="card-description">{{ type.description }}</p>
              </div>
              <div class="card-action">
                <span>Zeit reservieren</span>
                <v-icon right>mdi-arrow-right</v-icon>
              </div>
            </v-card>
          </div>
        </section>

        <div v-if="!isLoadingTypes && !hasReservationOptions" class="reservation-unavailable-card" role="status">
          <span class="reservation-unavailable-icon"><v-icon>mdi-information-outline</v-icon></span>
          <div>
            <h2>Keine Selbstreservierung in diesem Studio</h2>
            <p>
              Dieses Studio bietet aktuell keine Geräte oder Angebote zur Selbstreservierung an. Persönliche Trainertermine erscheinen automatisch
              hier, sobald sie vereinbart wurden.
            </p>
          </div>
        </div>
      </section>
    </v-container>
    <v-dialog v-model="ratingDialog" max-width="560" content-class="rating-dialog" @click:outside="closeRatingDialog">
      <v-card dark class="rating-card">
        <button type="button" class="rating-close" aria-label="Bewertungsdialog schliessen" @click="closeRatingDialog">
          <v-icon>mdi-close</v-icon>
        </button>
        <v-card-text class="rating-card-content">
          <span class="rating-dialog-icon"><v-icon>mdi-star-shooting-outline</v-icon></span>
          <span class="section-kicker">DEIN FEEDBACK</span>
          <h2>Wie war dein Termin?</h2>
          <p v-if="ratingAppointment" class="rating-appointment">
            {{ ratingAppointment.title }} · {{ formatAppointmentDate(ratingAppointment) }} · {{ appointmentTime(ratingAppointment) }}
          </p>

          <v-rating
            v-model="ratingForm.stars"
            class="rating-stars"
            color="amber"
            background-color="rgba(255,255,255,.16)"
            hover
            large
            :aria-label="ratingForm.stars ? `${ratingForm.stars} von 5 Sternen` : 'Sterne auswählen'"></v-rating>
          <strong class="rating-caption">{{ ratingCaption }}</strong>

          <div class="rating-tags">
            <span class="rating-field-label">Was hat dir gefallen?</span>
            <div class="rating-tag-list">
              <button
                v-for="tag in ratingTagOptions"
                :key="tag.value"
                type="button"
                class="rating-tag"
                :class="{ 'rating-tag--selected': ratingForm.tags.includes(tag.value) }"
                :aria-pressed="ratingForm.tags.includes(tag.value)"
                @click="toggleRatingTag(tag.value)">
                <v-icon x-small>{{ tag.icon }}</v-icon>
                {{ tag.label }}
              </button>
            </div>
          </div>

          <v-textarea
            v-model="ratingForm.comment"
            class="rating-comment"
            outlined
            auto-grow
            rows="3"
            counter="1000"
            maxlength="1000"
            :required="ratingCommentRequired"
            :label="ratingCommentLabel"
            :hint="ratingCommentHint"
            persistent-hint></v-textarea>

          <div class="rating-actions">
            <v-btn text class="text-none" :disabled="isSavingRating" @click="closeRatingDialog">Später</v-btn>
            <v-btn
              color="primary"
              large
              depressed
              class="text-none rating-submit"
              :loading="isSavingRating"
              :disabled="!canSubmitRating"
              @click="saveRating">
              <v-icon left small>mdi-check</v-icon>
              {{ ratingAppointment && ratingAppointment.rating ? "Bewertung aktualisieren" : "Bewertung senden" }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <ActionDialog
      :show="showDialog"
      :title="dialog.title"
      :text="dialog.text"
      :confirm-button-text="dialog.confirmText"
      :icon="dialog.icon"
      @close="showDialog = false"
      @confirm="handleDialogConfirm" />
    <upgrade-contract
      :useCustomer="true"
      @hide="showContractDialog = false"
      @created="upgradeCreatedDialog = true"
      :dialog="showContractDialog"></upgrade-contract>
    <v-dialog persistent max-width="500" v-model="upgradeCreatedDialog">
      <v-card dark>
        <v-card-title class="primary white--text">Zusatzpaket Upgrade</v-card-title>
        <v-card-text class="mt-5"
          >Vielen Dank für dein Zusatzpaket Upgrade!
          <br />
          Sobald die Aktivierung abgeschlossen ist, erhältst du eine Bestätigung per E-Mail sowie die entsprechende Rechnung.
          <br /><br />
          Bei Fragen zum Vertrag erreichst du uns unter {{ selectedStudio?.telefon || "" }} oder schreibe uns eine Email an
          {{ selectedStudio?.email || "info@wellcomefit.ch" }}.
          <br />
          <br />
          Bis bald im {{ selectedStudio?.art?.replace("AG", "") || "well come FIT" }}
        </v-card-text>
        <v-card-actions class="d-flex justify-center">
          <v-btn text @click="upgradeCreatedDialog = false">Alles klar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { mapState, mapGetters } from "vuex"
import ActionDialog from "../components/ActionDialog.vue"
import UpgradeContract from "@/views/dashboard/contract/components/upgradeContract.vue"
export default {
  components: {
    ActionDialog,
    UpgradeContract,
  },
  data() {
    return {
      showContractDialog: false,
      upgradeCreatedDialog: false,
      reservationTypes: [],
      myReservations: [],
      upcomingTrainerAppointments: [],
      pastTrainerAppointments: [],

      isLoadingTypes: true,
      isLoadingReservations: true,
      isLoadingAppointments: true,
      appointmentsError: "",
      showPastAppointments: false,

      ratingDialog: false,
      ratingAppointment: null,
      handledRatingRequest: "",
      isSavingRating: false,
      ratingForm: {
        stars: 0,
        tags: [],
        comment: "",
      },
      ratingTagOptions: [
        { value: "friendly", label: "Freundlich", icon: "mdi-emoticon-happy-outline" },
        { value: "competent", label: "Kompetent", icon: "mdi-school-outline" },
        { value: "helpful", label: "Hilfreich", icon: "mdi-hand-heart-outline" },
        { value: "motivating", label: "Motivierend", icon: "mdi-arm-flex-outline" },
        { value: "punctual", label: "Pünktlich", icon: "mdi-clock-check-outline" },
        { value: "pleasant", label: "Angenehm", icon: "mdi-heart-outline" },
      ],

      showDialog: false,
      dialog: {
        title: "",
        text: "",
        confirmText: "",
        action: null,
        icon: "mdi-lock-alert-outline",
        cancellationId: null,
      },
      isCancelling: false,
    }
  },
  async created() {
    this.fetchReservationTypes()
    this.fetchMyReservations()
    this.fetchMyCalendarAppointments()
  },
  computed: {
    ...mapGetters("userStore", ["activeContractDetails"]),
    ...mapState("studioStore", ["selectedStudio"]),
    hasReservationOptions() {
      return this.reservationTypes.length > 0
    },
    pageSubtitle() {
      if (this.isLoadingTypes || this.hasReservationOptions) {
        return "Termine ansehen und Angebote reservieren."
      }
      return "Deine persönlichen Termine auf einen Blick."
    },
    hubPrimaryColor() {
      const theme = this.$vuetify && this.$vuetify.theme
      const themes = theme && theme.themes
      const currentTheme = themes && (theme.dark ? themes.dark : themes.light)
      const primary = (currentTheme && currentTheme.primary) || (themes && themes.light && themes.light.primary)
      return typeof primary === "string" && primary.trim() ? primary.trim() : "#009640"
    },
    hubPrimaryRgb() {
      const value = this.hubPrimaryColor.replace("#", "")
      const normalized =
        value.length === 3
          ? value
              .split("")
              .map((part) => part + part)
              .join("")
          : value.slice(0, 6)
      if (!/^[0-9a-f]{6}$/i.test(normalized)) return "0, 150, 64"
      return [0, 2, 4].map((index) => parseInt(normalized.slice(index, index + 2), 16)).join(", ")
    },
    hubThemeStyle() {
      return {
        "--hub-primary": this.hubPrimaryColor,
        "--hub-primary-rgb": this.hubPrimaryRgb,
        "--v-primary": this.hubPrimaryColor,
        "--primary": this.hubPrimaryColor,
      }
    },
    upcomingBookingItems() {
      const reservationItems = this.myReservations.map((reservation) => {
        const start = reservation.startTime ? this.$moment.utc(reservation.startTime) : null
        return {
          key: `reservation-${reservation._id}`,
          kind: "reservation",
          reservation,
          startsAt: start && start.isValid() ? start.valueOf() : Number.MAX_SAFE_INTEGER,
        }
      })
      const appointmentItems = this.upcomingTrainerAppointments.map((appointment) => {
        const start = this.appointmentMoment(appointment)
        return {
          key: `appointment-${appointment._id}`,
          kind: "appointment",
          appointment,
          startsAt: start ? start.valueOf() : Number.MAX_SAFE_INTEGER,
        }
      })
      return [...reservationItems, ...appointmentItems].sort((first, second) => first.startsAt - second.startsAt)
    },
    ratingCaption() {
      const captions = {
        1: "Das können wir besser",
        2: "Da ist noch Luft nach oben",
        3: "Alles in Ordnung",
        4: "Richtig guter Termin",
        5: "Ausgezeichnet!",
      }
      return captions[this.ratingForm.stars] || "Tippe auf die Sterne"
    },
    ratingCommentRequired() {
      const stars = Number(this.ratingForm.stars)
      return Number.isInteger(stars) && stars >= 1 && stars < 4
    },
    ratingCommentMissing() {
      return this.ratingCommentRequired && !String(this.ratingForm.comment || "").trim()
    },
    ratingCommentLabel() {
      return this.ratingCommentRequired ? "Was können wir besser machen? (Pflichtfeld)" : "Möchtest du uns noch etwas sagen? (optional)"
    },
    ratingCommentHint() {
      return this.ratingCommentRequired ? "Bei weniger als vier Sternen benötigen wir einen kurzen Kommentar." : "Dein Kommentar ist freiwillig."
    },
    canSubmitRating() {
      return Boolean(this.ratingForm.stars) && !this.ratingCommentMissing
    },
  },
  methods: {
    async fetchMyCalendarAppointments() {
      this.isLoadingAppointments = true
      this.appointmentsError = ""
      try {
        const response = await api.reservationTool.getMyCalendarAppointments()
        const data = response.data || {}
        this.upcomingTrainerAppointments = Array.isArray(data.upcoming) ? data.upcoming : []
        this.pastTrainerAppointments = Array.isArray(data.past) ? data.past : []
        this.openRequestedRating()
      } catch (error) {
        const responseData = error && error.response ? error.response.data : null
        this.appointmentsError = (responseData && responseData.message) || "Bitte prüfe deine Verbindung und versuche es noch einmal."
        console.error("Fehler beim Laden der eigenen Trainertermine:", error)
      } finally {
        this.isLoadingAppointments = false
      }
    },
    openRatingDialog(appointment) {
      this.ratingAppointment = appointment
      this.ratingForm = {
        stars: appointment.rating ? Number(appointment.rating.stars) : 0,
        tags: appointment.rating && Array.isArray(appointment.rating.tags) ? [...appointment.rating.tags] : [],
        comment: appointment.rating ? appointment.rating.comment || "" : "",
      }
      this.ratingDialog = true
    },
    openRequestedRating() {
      const requestedId = String(this.$route?.query?.rate || "")
      if (!requestedId || requestedId === this.handledRatingRequest) return
      const appointment = this.pastTrainerAppointments.find((entry) => String(entry._id) === requestedId)
      if (!appointment || !appointment.canRate) return

      this.handledRatingRequest = requestedId
      this.showPastAppointments = true
      this.openRatingDialog(appointment)

      const query = { ...this.$route.query }
      delete query.rate
      const navigation = this.$router.replace({ path: this.$route.path, query })
      if (navigation && typeof navigation.catch === "function") navigation.catch(() => {})
    },
    closeRatingDialog() {
      if (this.isSavingRating) return
      this.ratingDialog = false
      this.ratingAppointment = null
      this.ratingForm = { stars: 0, tags: [], comment: "" }
    },
    toggleRatingTag(tag) {
      if (this.ratingForm.tags.includes(tag)) {
        this.ratingForm.tags = this.ratingForm.tags.filter((value) => value !== tag)
      } else if (this.ratingForm.tags.length < 5) {
        this.ratingForm.tags = [...this.ratingForm.tags, tag]
      }
    },
    async saveRating() {
      if (!this.ratingAppointment || !this.ratingForm.stars || this.isSavingRating) return
      if (this.ratingCommentMissing) {
        this.$toast.error("Bitte ergänze bei weniger als vier Sternen einen kurzen Kommentar.")
        return
      }
      this.isSavingRating = true
      try {
        const response = await api.reservationTool.rateCalendarAppointment(this.ratingAppointment._id, {
          stars: this.ratingForm.stars,
          tags: this.ratingForm.tags,
          comment: this.ratingForm.comment,
        })
        const updatedRating = response.data && response.data.rating
        const updateAppointment = (appointment) =>
          appointment._id === this.ratingAppointment._id ? { ...appointment, rating: updatedRating } : appointment
        this.upcomingTrainerAppointments = this.upcomingTrainerAppointments.map(updateAppointment)
        this.pastTrainerAppointments = this.pastTrainerAppointments.map(updateAppointment)
        this.$toast.success(this.ratingAppointment.rating ? "Bewertung aktualisiert." : "Danke für dein Feedback!")
        this.ratingDialog = false
        this.ratingAppointment = null
        this.ratingForm = { stars: 0, tags: [], comment: "" }
      } catch (error) {
        const responseData = error && error.response ? error.response.data : null
        this.$toast.error((responseData && responseData.message) || "Die Bewertung konnte nicht gespeichert werden.")
        console.error("Fehler beim Speichern der Terminbewertung:", error)
      } finally {
        this.isSavingRating = false
      }
    },
    appointmentMoment(appointment) {
      if (!appointment || !appointment.date) return null
      const parsed = this.$moment(`${appointment.date} ${appointment.start}`, "YYYY-MM-DD HH:mm", true)
      return parsed.isValid() ? parsed : null
    },
    appointmentDay(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("DD") : "--"
    },
    appointmentMonth(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("MMM").replace(".", "").toUpperCase() : "---"
    },
    appointmentWeekday(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("ddd").replace(".", "").toUpperCase() : ""
    },
    appointmentWeekdayLong(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("dddd") : ""
    },
    formatAppointmentDate(appointment) {
      const date = this.appointmentMoment(appointment)
      return date ? date.format("DD.MM.YYYY") : appointment.date || ""
    },
    relativeAppointmentDate(appointment) {
      const date = this.appointmentMoment(appointment)
      if (!date) return ""
      const difference = date.clone().startOf("day").diff(this.$moment().startOf("day"), "days")
      if (difference === 0) return "Heute"
      if (difference === 1) return "Morgen"
      if (difference > 1 && difference <= 7) return `In ${difference} Tagen`
      return this.appointmentWeekdayLong(appointment)
    },
    appointmentTime(appointment) {
      if (!appointment) return ""
      return appointment.end ? `${appointment.start} – ${appointment.end} Uhr` : `${appointment.start} Uhr`
    },
    trainerName(appointment) {
      const trainer = appointment && appointment.trainer
      if (!trainer) return "Trainer wird noch zugeteilt"
      return `${trainer.firstName || ""} ${trainer.lastName || ""}`.trim() || "Trainer wird noch zugeteilt"
    },
    appointmentLocation(appointment) {
      const studio = appointment && appointment.studio
      return (studio && (studio.name || studio.address)) || this.selectedStudio?.name || "Dein Studio"
    },
    appointmentAccent(appointment) {
      const color = appointment && appointment.color
      return typeof color === "string" && /^#[0-9a-f]{3,8}$/i.test(color) ? color : this.hubPrimaryColor
    },
    isInactiveAppointment(appointment) {
      return appointment.status === "cancelled" || appointment.status === "noshow"
    },
    promptCancelReservation(reservation) {
      this.dialog = {
        title: "Reservierung stornieren?",
        text: `Möchtest du die Reservierung für <strong>${reservation.reservationType.name}</strong> am<br>${this.formatDateTime(
          reservation.startTime,
        )} wirklich stornieren?`,
        confirmText: "Ja, stornieren",
        confirmButtonColor: "red",
        action: "confirmCancel",
        icon: "mdi-trash-can-outline",
        cancellationId: reservation._id,
      }
      this.showDialog = true
    },
    async executeCancellation() {
      if (!this.dialog.cancellationId) return
      this.isCancelling = true

      try {
        await api.reservationTool.deleteReservation(this.dialog.cancellationId)
        this.myReservations = this.myReservations.filter((rez) => rez._id !== this.dialog.cancellationId)
        this.$toast.success("Reservierung erfolgreich storniert!")
      } catch (error) {
        console.error("Fehler bei der Stornierung:", error)
      } finally {
        this.isCancelling = false
        this.dialog.cancellationId = null
      }
    },
    async fetchReservationTypes() {
      this.isLoadingTypes = true
      try {
        const response = await api.reservationTool.getReservationTypes(this.selectedStudio?._id)
        const types = Array.isArray(response.data) ? response.data : []
        this.reservationTypes = types.filter((type) => type.active)
      } catch (error) {
        console.error("Fehler beim Laden der Reservierungstypen:", error)
      } finally {
        this.isLoadingTypes = false
      }
    },
    async fetchMyReservations() {
      this.isLoadingReservations = true
      try {
        const response = await api.reservationTool.getMyReservations()
        this.myReservations = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.error("Fehler beim Laden der eigenen Reservierungen:", error)
      } finally {
        this.isLoadingReservations = false
      }
    },
    selectType(type) {
      if (!type.membersOnly) {
        this.navigateToBooking(type)
        return
      }

      if (!this.activeContractDetails) {
        this.showSubscriptionNeededDialog()
        return
      }

      const requiredSubs = (type.requiredSubscriptionTypes || []).map((s) => s.toLowerCase())
      const requiredAddons = (type.requiredAddonPackages || []).map((p) => p.toLowerCase())

      if (requiredSubs.length === 0 && requiredAddons.length === 0) {
        this.navigateToBooking(type)
        return
      }

      const userSubscriptionTitle = (this.activeContractDetails.title || "").toLowerCase()
      const userActiveAddons = (this.activeContractDetails.activeAddonsTitles || []).map((e) => e.toLowerCase())

      if (
        userSubscriptionTitle.includes("mitarbeiter") ||
        ((userSubscriptionTitle.includes("erwachsene") ||
          userSubscriptionTitle.includes("flexi winti") ||
          userSubscriptionTitle.includes("ahv") ||
          (userSubscriptionTitle.includes("jahres abo") && !userSubscriptionTitle.includes("kombi"))) &&
          !requiredSubs.some((reqSub) => reqSub.includes("pelvi"))) ||
        (userSubscriptionTitle.includes("premium") && !requiredSubs.some((reqSub) => reqSub.includes("pelvi"))) ||
        (userSubscriptionTitle.includes("vip") && !requiredSubs.some((reqSub) => reqSub.includes("pelvi")))
      ) {
        this.navigateToBooking(type)
        return
      }

      let hasPermission = false

      if (requiredSubs.length > 0) {
        hasPermission = requiredSubs.some((reqSub) => userSubscriptionTitle.includes(reqSub))
      }

      if (!hasPermission && requiredAddons.length > 0) {
        hasPermission = requiredAddons.some((reqAddon) => userActiveAddons.some((userAddon) => userAddon.toLowerCase().includes(reqAddon)))
      }

      if (hasPermission) {
        this.navigateToBooking(type)
      } else {
        this.showUpgradeNeededDialog()
      }
    },

    showSubscriptionNeededDialog() {
      this.dialog = {
        title: "Aktives Abo benötigt",
        text: "Für diese Buchung wird ein aktives Abo benötigt.<br>Bitte löse zuerst ein Abo, um fortzufahren.",
        confirmText: "Abo lösen",
        action: "navigateToContracts",
        icon: "mdi-star-circle-outline",
      }
      this.showDialog = true
    },

    showUpgradeNeededDialog() {
      this.dialog = {
        title: "Zusatzpaket erforderlich",
        text: "Für diese Buchung wird ein Zusatzpaket benötigt.<br>Bitte löse zuerst das Zusatzpaket, um fortzufahren.",
        confirmText: "zu den Upgrades",
        action: "navigateToUpgrades",
        icon: "mdi-arrow-up-bold-circle-outline",
      }
      this.showDialog = true
    },

    handleDialogConfirm() {
      if (this.dialog.action === "navigateToContracts") {
        this.$router.push("/contract?studio=" + this.selectedStudio?.code)
      } else if (this.dialog.action === "navigateToUpgrades") {
        this.showContractDialog = true
      } else if (this.dialog.action === "confirmCancel") {
        this.executeCancellation()
      }

      this.showDialog = false
    },

    navigateToBooking(type) {
      this.$router.push({
        name: "ReservationBooking",
        params: { reservationTypeId: type._id },
      })
    },
    formatDateTime(dateTime) {
      return this.$moment.utc(dateTime).format("dd, DD.MM.YY [um] HH:mm [Uhr]")
    },
  },
}
</script>

<style scoped>
.reservation-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
}

.cancel-btn {
  color: var(--text-secondary) !important;
}

.cancel-btn:hover {
  color: #ff5252 !important;
}
.selection-shell {
  --primary: var(--hub-primary, #009640);
  --bg: #121212;
  --card-bg: #1e1e1e;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border: #2c2c2c;
  border-radius: 0px !important;
  background-color: var(--bg);
  min-height: 100vh;
  padding-top: 40px;
  padding-bottom: 40px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* --- TERMINE OBEN --- */
.upcoming-section {
  max-width: 600px;
  margin: 0 auto 40px auto;
}
.upcoming-section-empty {
  max-width: 600px;
  margin: 40px auto 0 auto;
}
.section-divider {
  max-width: 600px;
  margin: 0 auto 20px auto;
}
.section-headline {
  font-size: 1.5rem;
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
  margin-bottom: 20px;
  text-align: center;
}
.reservations-list {
  display: grid;
  gap: 12px;
}
.reservation-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background: var(--card-bg) !important;
  border: 1px solid var(--border) !important;
  border-radius: 12px !important;
}
.rez-icon {
  margin-right: 16px;
}
.rez-details {
  display: flex;
  flex-direction: column;
}

.is-cancelled {
  opacity: 0.6;
  background: repeating-linear-gradient(45deg, var(--card-bg), var(--card-bg) 10px, #2a2a2a 10px, #2a2a2a 20px) !important;
}

.is-cancelled .rez-title {
  text-decoration: line-through;
}
.rez-title {
  font-weight: 600;
  color: var(--text-primary);
}
.rez-time {
  font-size: 0.9rem;
  color: var(--text-secondary);
}
.no-reservations-placeholder {
  text-align: center;
  padding: 30px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  border: 2px dashed var(--border);
}
.placeholder-icon {
  font-size: 3rem !important;
  color: var(--text-secondary) !important;
  margin-bottom: 12px;
}
.no-reservations-placeholder p {
  color: var(--text-secondary);
  font-weight: 500;
}

/* --- HEADER --- */
.header-section {
  margin-bottom: 40px;
}
.headline {
  font-size: clamp(2rem, 6vw, 2.5rem);
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
}
.sub-headline {
  font-size: 1.1rem;
  color: var(--text-secondary);
}

/* --- TYPE FEED --- */
.type-feed {
  display: grid;
  gap: 20px;
  max-width: 600px;
  margin: auto;
}
.type-card {
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border-radius: 20px !important;
  border: 1px solid var(--border);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.type-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
}

.card-content {
  padding: 24px;
  flex-grow: 1;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-title {
  font-size: 1.4rem;
  font-weight: 1000;
  text-transform: uppercase;
  font-style: italic;
  color: var(--text-primary);
}

.duration-chip {
  background-color: rgba(255, 255, 255, 0.05) !important;
  color: var(--text-secondary) !important;
  font-weight: 500;
}

.card-description {
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
}

.card-action {
  background-color: var(--v-primary);
  color: var(--text-primary);
  padding: 16px 24px;
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  transition: background-color 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
}

.type-card:hover .card-action {
  background-color: var(--v-primary);
}

.card-action .v-icon {
  transition: transform 0.3s ease;
}

.type-card:hover .card-action .v-icon {
  transform: translateX(5px);
}

/* --- REFRESHED CUSTOMER HUB --- */
.selection-shell {
  --hub-primary: #009640;
  --hub-primary-rgb: 0, 150, 64;
  --bg: #0d0f13;
  --card-bg: #171a20;
  --card-bg-high: #1d2129;
  --text-primary: #ffffff;
  --text-secondary: #9da4b2;
  --border: rgba(255, 255, 255, 0.09);
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  padding: 34px 16px 72px;
  background: radial-gradient(circle at 92% -8%, rgba(var(--hub-primary-rgb), 0.1), transparent 28rem),
    radial-gradient(circle at -12% 38%, rgba(255, 255, 255, 0.035), transparent 24rem), linear-gradient(160deg, #0b0d11 0%, #11141a 48%, #0d0f13 100%);
}

.selection-shell::before {
  content: "";
  position: absolute;
  top: -170px;
  left: 50%;
  width: 520px;
  height: 280px;
  border-radius: 50%;
  transform: translateX(-50%);
  background: var(--hub-primary);
  opacity: 0.08;
  filter: blur(100px);
  pointer-events: none;
}

.hub-container {
  position: relative;
  z-index: 1;
  max-width: 980px !important;
}

.hub-header {
  max-width: 720px;
  margin: 18px auto 36px;
}

.hub-eyebrow,
.section-kicker {
  display: block;
  margin-bottom: 8px;
  color: var(--hub-primary);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.18em;
}

.headline {
  font-size: clamp(2.15rem, 7vw, 3.6rem);
  font-weight: 900;
  font-style: normal;
  line-height: 1.02;
  letter-spacing: -0.045em;
  text-transform: none;
}

.sub-headline {
  max-width: 620px;
  margin: auto;
  color: var(--text-secondary);
  font-size: clamp(1rem, 2vw, 1.125rem);
  line-height: 1.55;
}

.hub-tab,
.scope-switch button,
.rating-summary button,
.rating-tag,
.rating-close {
  appearance: none;
  border: 0;
  font: inherit;
  background: transparent;
  cursor: pointer;
}

.hub-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  max-width: 820px;
  margin: 0 auto 28px;
  padding: 7px;
  border: 1px solid var(--border);
  border-radius: 24px;
  background: rgba(16, 18, 24, 0.78);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.24);
  backdrop-filter: blur(18px);
}

.hub-tab {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid transparent;
  border-radius: 18px;
  color: var(--text-secondary);
  text-align: left;
  background: transparent;
  transition: 180ms ease;
}

.hub-tab:hover {
  border-color: rgba(var(--hub-primary-rgb), 0.14);
  background: rgba(var(--hub-primary-rgb), 0.055);
}

.hub-tab--active {
  border-color: rgba(var(--hub-primary-rgb), 0.28);
  color: var(--text-primary);
  background: linear-gradient(145deg, rgba(var(--hub-primary-rgb), 0.14), rgba(255, 255, 255, 0.04));
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25), inset 0 0 0 1px rgba(var(--hub-primary-rgb), 0.06);
}

.hub-tab:focus-visible,
.scope-switch button:focus-visible,
.rating-tag:focus-visible,
.rating-summary button:focus-visible,
.rating-close:focus-visible {
  outline: 2px solid var(--hub-primary);
  outline-offset: 2px;
}

.hub-tab-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.06);
}

.hub-tab-icon .v-icon {
  color: var(--text-secondary);
}

.hub-tab--active .hub-tab-icon {
  color: var(--hub-primary);
  background: rgba(var(--hub-primary-rgb), 0.14);
}

.hub-tab--active .hub-tab-icon .v-icon {
  color: var(--hub-primary);
}

.hub-tab-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.hub-tab-copy strong {
  color: inherit;
  font-size: 1rem;
  font-weight: 800;
}

.hub-tab-copy small {
  overflow: hidden;
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hub-tab-count {
  display: grid;
  min-width: 27px;
  height: 27px;
  padding: 0 7px;
  place-items: center;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 800;
  background: rgba(0, 0, 0, 0.22);
}

.hub-tab--active .hub-tab-count {
  border-color: rgba(var(--hub-primary-rgb), 0.3);
  background: rgba(var(--hub-primary-rgb), 0.14);
}

.appointments-view,
.reservations-view {
  max-width: 900px;
  margin: auto;
}

.hub-loading {
  display: flex;
  min-height: 330px;
  flex-direction: column;
  gap: 18px;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.hub-loading--compact {
  min-height: 190px;
}

.state-card {
  display: flex;
  gap: 18px;
  align-items: center;
  max-width: 760px;
  margin: 34px auto;
  padding: 24px;
  border: 1px solid var(--border);
  border-radius: 22px;
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.045);
}

.state-card--error {
  border-color: rgba(255, 82, 82, 0.28);
}

.state-card--empty {
  justify-content: center;
  padding: 42px 28px;
  text-align: left;
}

.state-icon {
  display: grid;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  place-items: center;
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.07);
}

.state-icon .v-icon {
  font-size: 1.6875rem;
}

.state-card h2 {
  margin: 0 0 5px;
  font-size: 1.125rem;
}

.state-card p {
  margin: 0;
  color: var(--text-secondary);
}

.state-card .v-btn {
  margin-left: auto;
}

.next-appointment {
  --appointment-accent: var(--hub-primary);
  position: relative;
  isolation: isolate;
  overflow: hidden;
  margin-bottom: 28px;
  padding: clamp(22px, 4vw, 34px);
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 28px;
  color: var(--text-primary);
  background: linear-gradient(135deg, color-mix(in srgb, var(--appointment-accent) 18%, #191d25), #151820 62%), #171a20;
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.32);
}

@supports not (background: color-mix(in srgb, red 10%, black)) {
  .next-appointment {
    background: linear-gradient(135deg, #232832, #151820 62%);
  }
}

.next-appointment::after {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 4px;
  background: var(--appointment-accent);
}

.next-appointment-glow {
  position: absolute;
  z-index: -1;
  top: -110px;
  right: -90px;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: var(--appointment-accent);
  opacity: 0.17;
  filter: blur(80px);
}

.next-topline,
.next-meta,
.collection-header,
.appointment-card-title-row,
.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.next-label {
  display: inline-flex;
  gap: 7px;
  align-items: center;
  color: var(--appointment-accent);
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.next-label .v-icon {
  color: var(--appointment-accent);
}

.next-content {
  display: flex;
  gap: clamp(18px, 4vw, 30px);
  align-items: center;
  padding: 30px 0;
}

.date-tile {
  display: flex;
  width: 92px;
  height: 106px;
  flex: 0 0 92px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.18);
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.05);
}

.date-tile span,
.date-tile small {
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.11em;
}

.date-tile strong {
  margin: 3px 0;
  font-size: 2.35rem;
  font-weight: 900;
  line-height: 1;
}

.relative-date {
  color: var(--appointment-accent);
  font-size: 0.78rem;
  font-weight: 800;
}

.next-copy h2 {
  max-width: 600px;
  margin: 4px 0 10px;
  font-size: clamp(1.6rem, 4vw, 2.35rem);
  font-weight: 900;
  line-height: 1.1;
  letter-spacing: -0.035em;
}

.next-time {
  display: flex;
  gap: 8px;
  align-items: center;
  color: #d9dde5;
}

.next-time .v-icon {
  color: var(--appointment-accent);
}

.next-meta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 16px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.meta-person,
.meta-location {
  display: flex;
  min-width: 0;
  gap: 11px;
  align-items: center;
}

.meta-avatar,
.meta-icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.11);
  background: rgba(255, 255, 255, 0.06);
}

.meta-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
}

.meta-person div,
.meta-location div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.meta-person small,
.meta-location small {
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.meta-person strong,
.meta-location strong {
  color: var(--text-primary);
  font-size: 1rem;
  overflow-wrap: anywhere;
}

.appointment-collection {
  padding: 28px;
  border: 1px solid var(--border);
  border-radius: 26px;
  background: rgba(19, 22, 28, 0.72);
  backdrop-filter: blur(18px);
}

.collection-header {
  gap: 22px;
  margin-bottom: 22px;
}

.collection-header h2,
.reservation-intro h2 {
  margin: 0;
  color: var(--text-primary);
  font-size: clamp(1.35rem, 3vw, 1.75rem);
  font-weight: 900;
  letter-spacing: -0.025em;
}

.scope-switch {
  display: flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: 13px;
  background: rgba(0, 0, 0, 0.18);
}

.scope-switch button {
  padding: 8px 11px;
  border-radius: 9px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 750;
  transition: 150ms ease;
}

.scope-switch button.active {
  color: var(--text-primary);
  background: rgba(var(--hub-primary-rgb), 0.16);
  box-shadow: inset 0 0 0 1px rgba(var(--hub-primary-rgb), 0.22);
}

.scope-switch span {
  display: inline-grid;
  min-width: 20px;
  height: 20px;
  margin-left: 4px;
  place-items: center;
  border-radius: 999px;
  font-size: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
}

.scope-switch button.active span {
  background: rgba(var(--hub-primary-rgb), 0.22);
}

.trainer-appointment-list {
  display: grid;
  gap: 10px;
}

.trainer-appointment-card {
  --appointment-accent: var(--hub-primary);
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  overflow: hidden;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.035);
  transition: 180ms ease;
}

.trainer-appointment-card::before {
  content: "";
  position: absolute;
  inset: 12px auto 12px 0;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--appointment-accent);
}

.trainer-appointment-card--muted {
  opacity: 0.62;
}

.compact-date {
  display: flex;
  width: 54px;
  height: 56px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
}

.compact-date strong {
  color: var(--text-primary);
  font-size: 1.25rem;
  line-height: 1;
}

.compact-date span {
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 900;
}

.appointment-card-copy {
  min-width: 0;
}

.appointment-card-title-row {
  gap: 10px;
  justify-content: flex-start;
}

.appointment-card-title-row h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.appointment-card-time {
  margin: 6px 0 8px;
  color: #c5cad3;
  font-size: 1rem;
  line-height: 1.4;
}

.appointment-card-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
}

.appointment-card-facts span {
  display: inline-flex;
  gap: 4px;
  align-items: center;
}

.appointment-card-facts .v-icon {
  color: var(--text-secondary);
}

.appointment-card-action {
  display: flex;
  justify-content: flex-end;
}

.rating-summary {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.rating-summary button {
  margin-top: 3px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.collection-empty {
  display: flex;
  min-height: 118px;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  padding: 20px;
  border: 1px dashed var(--border);
  border-radius: 16px;
  color: var(--text-secondary);
  text-align: center;
}

.collection-empty p {
  margin: 0;
}

.reservation-intro {
  display: flex;
  gap: 18px;
  align-items: center;
  margin-bottom: 26px;
  padding: 24px 26px;
  border: 1px solid rgba(var(--hub-primary-rgb), 0.18);
  border-radius: 24px;
  background: linear-gradient(145deg, rgba(var(--hub-primary-rgb), 0.08), rgba(255, 255, 255, 0.025));
}

.reservation-intro-icon,
.type-icon {
  display: grid;
  place-items: center;
  border: 1px solid rgba(var(--hub-primary-rgb), 0.24);
  color: var(--hub-primary);
  background: rgba(var(--hub-primary-rgb), 0.12);
}

.reservation-intro-icon {
  width: 58px;
  height: 58px;
  flex: 0 0 58px;
  border-radius: 18px;
}

.reservation-intro-icon .v-icon,
.type-icon .v-icon {
  color: var(--hub-primary);
}

.reservation-intro p {
  margin: 6px 0 0;
  color: var(--text-secondary);
  line-height: 1.5;
}

.upcoming-section,
.upcoming-section-empty,
.section-divider,
.type-feed {
  max-width: none;
}

.upcoming-section {
  margin-bottom: 34px;
}

.upcoming-section-empty {
  margin-top: 30px;
}

.section-divider {
  margin: 0 0 18px;
}

.section-title-row .section-headline,
.section-divider .section-headline {
  margin: 0;
  font-size: 1.35rem;
  font-style: normal;
  font-weight: 900;
  text-align: left;
  text-transform: none;
}

.reservations-list {
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 330px), 1fr));
}

.reservation-card {
  min-width: 0;
  padding: 18px !important;
  border-radius: 18px !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.065), rgba(255, 255, 255, 0.025)) !important;
  box-shadow: none !important;
}

.rez-icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  margin-right: 13px;
  place-items: center;
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.06);
}

.rez-title {
  overflow: hidden;
  font-weight: 800;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-cancelled {
  background: rgba(255, 255, 255, 0.025) !important;
}

.type-feed {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.type-card {
  min-height: 225px;
  border-radius: 22px !important;
  background: linear-gradient(145deg, #1c2028, #14171c) !important;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.22);
}

.type-card:hover {
  border-color: rgba(var(--hub-primary-rgb), 0.35);
}

.card-header {
  margin-bottom: 20px;
}

.type-icon {
  width: 46px;
  height: 46px;
  border-radius: 15px;
}

.card-title {
  margin-bottom: 9px;
  font-size: 1.375rem;
  font-style: normal;
  letter-spacing: -0.02em;
  text-transform: none;
}

.card-description {
  margin-bottom: 0;
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.55;
}

.card-action {
  padding: 14px 20px;
  background: var(--hub-primary);
}

.booking-options-section {
  margin-top: 46px;
  padding-top: 38px;
  border-top: 1px solid var(--border);
}

.section-divider--booking {
  margin-bottom: 20px;
}

.section-help {
  max-width: 650px;
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.5;
}

.bookings-overview {
  margin: 0;
  padding-top: 0;
  border-top: 0;
}

.bookings-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 390px), 1fr));
  gap: 14px;
  margin-top: 18px;
}

.booking-card {
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  min-width: 0;
  min-height: 124px;
  overflow: hidden;
  padding: 20px !important;
  border: 1px solid var(--border) !important;
  border-radius: 18px !important;
  color: var(--text-primary) !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.025)) !important;
  box-shadow: none !important;
}

.booking-card--appointment {
  grid-template-columns: auto minmax(0, 1fr);
  border-color: rgba(var(--hub-primary-rgb), 0.2) !important;
}

.booking-card--appointment::before {
  content: "";
  position: absolute;
  inset: 12px auto 12px 0;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--hub-primary);
}

.booking-card--muted {
  opacity: 0.66;
}

.booking-card-icon {
  display: grid;
  width: 46px;
  height: 46px;
  place-items: center;
  border: 1px solid rgba(var(--hub-primary-rgb), 0.18);
  border-radius: 14px;
  background: rgba(var(--hub-primary-rgb), 0.1);
}

.booking-card-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.booking-card-kind {
  margin-bottom: 5px;
  color: var(--hub-primary);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.booking-card-copy > strong {
  color: var(--text-primary);
  font-size: 1.125rem;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.booking-card-time {
  margin-top: 6px;
  color: #c8cdd6;
  font-size: 1rem;
  line-height: 1.4;
}

.booking-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-top: 8px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
}

.booking-card-meta > span {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  overflow-wrap: anywhere;
}

.booking-card-meta .v-icon {
  color: var(--text-secondary);
}

.booking-card-action {
  display: flex;
  justify-content: flex-end;
}

.booking-state-chip {
  align-self: flex-start;
  margin-top: 6px;
}

.duration-chip,
.booking-state-chip {
  height: auto !important;
  min-height: 28px;
  padding: 4px 10px !important;
  font-size: 0.75rem !important;
  line-height: 1.3;
}

.hub-loading--bookings {
  min-height: 140px;
}

.appointments-notice {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 18px;
  padding: 11px 13px;
  border: 1px solid rgba(var(--hub-primary-rgb), 0.18);
  border-radius: 13px;
  color: var(--text-secondary);
  background: rgba(var(--hub-primary-rgb), 0.07);
  font-size: 0.875rem;
}

.appointments-notice .v-icon {
  color: var(--hub-primary);
}

.appointments-notice button {
  appearance: none;
  margin-left: auto;
  border: 0;
  color: var(--hub-primary);
  font: inherit;
  font-weight: 800;
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
}

.appointment-history {
  margin-top: 12px;
}

.history-toggle {
  appearance: none;
  display: grid;
  width: 100%;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  gap: 13px;
  align-items: center;
  padding: 17px 19px;
  border: 1px solid var(--border);
  border-radius: 18px;
  color: var(--text-primary);
  font: inherit;
  text-align: left;
  background: rgba(255, 255, 255, 0.035);
  cursor: pointer;
  transition: 160ms ease;
}

.history-toggle:focus-visible {
  outline: 2px solid var(--hub-primary);
  outline-offset: 2px;
}

.history-toggle-icon {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  border-radius: 12px;
  background: rgba(var(--hub-primary-rgb), 0.1);
}

.history-toggle-icon .v-icon {
  color: var(--hub-primary);
}

.history-toggle-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.history-toggle-copy strong {
  font-size: 1rem;
}

.history-toggle-copy small {
  margin-top: 4px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.4;
}

.history-toggle-count {
  display: grid;
  min-width: 26px;
  height: 26px;
  padding: 0 7px;
  place-items: center;
  border-radius: 999px;
  color: var(--text-primary);
  background: rgba(var(--hub-primary-rgb), 0.16);
  font-size: 0.75rem;
  font-weight: 800;
}

.history-toggle-chevron {
  color: var(--text-secondary) !important;
  transition: transform 160ms ease;
}

.history-toggle-chevron--open {
  transform: rotate(180deg);
}

.appointment-history-panel {
  margin-top: 10px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.12);
}

.reservation-unavailable-card {
  display: flex;
  gap: 15px;
  align-items: flex-start;
  margin-top: 36px;
  padding: 19px 20px;
  border: 1px solid rgba(var(--hub-primary-rgb), 0.18);
  border-radius: 18px;
  color: var(--text-primary);
  background: linear-gradient(145deg, rgba(var(--hub-primary-rgb), 0.075), rgba(255, 255, 255, 0.025));
}

.reservation-unavailable-icon {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  border-radius: 13px;
  background: rgba(var(--hub-primary-rgb), 0.12);
}

.reservation-unavailable-icon .v-icon {
  color: var(--hub-primary);
}

.reservation-unavailable-card h2 {
  margin: 0 0 5px;
  font-size: 1.125rem;
  font-weight: 850;
}

.reservation-unavailable-card p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.5;
}

.no-reservations-placeholder {
  grid-column: 1 / -1;
  border-width: 1px;
  background: rgba(255, 255, 255, 0.025);
}

.no-reservations-placeholder--compact {
  padding: 18px;
}

.no-reservations-placeholder--compact .placeholder-icon {
  margin: 0 6px 0 0;
  font-size: 1.3rem !important;
}

.rating-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 26px !important;
  background: radial-gradient(circle at 50% 0, rgba(255, 193, 7, 0.13), transparent 16rem), #15181e !important;
}

.rating-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 34px !important;
  color: var(--text-primary) !important;
  text-align: center;
}

.rating-close {
  position: absolute;
  z-index: 2;
  top: 14px;
  right: 14px;
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}

.rating-dialog-icon {
  display: grid;
  width: 58px;
  height: 58px;
  margin-bottom: 14px;
  place-items: center;
  border: 1px solid rgba(255, 193, 7, 0.22);
  border-radius: 18px;
  background: rgba(255, 193, 7, 0.1);
}

.rating-dialog-icon .v-icon {
  color: #ffc107;
  font-size: 1.8125rem;
}

.rating-card h2 {
  margin: 0;
  font-size: 1.7rem;
  font-weight: 900;
  letter-spacing: -0.03em;
}

.rating-appointment {
  margin: 7px 0 17px;
  color: var(--text-secondary);
  font-size: 0.875rem;
}

.rating-stars {
  margin-bottom: 2px;
}

.rating-caption {
  min-height: 24px;
  color: #ffd45d;
  font-size: 0.875rem;
}

.rating-tags {
  width: 100%;
  margin: 24px 0 18px;
  text-align: left;
}

.rating-field-label {
  display: block;
  margin-bottom: 10px;
  color: #d7dbe3;
  font-size: 0.875rem;
  font-weight: 800;
}

.rating-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rating-tag {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  padding: 8px 11px;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-secondary);
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.035);
  transition: 150ms ease;
}

.rating-tag .v-icon {
  color: inherit;
}

.rating-tag--selected {
  border-color: rgba(255, 193, 7, 0.35);
  color: #ffda70;
  background: rgba(255, 193, 7, 0.11);
}

.rating-comment {
  width: 100%;
}

.rating-actions {
  display: flex;
  width: 100%;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
}

.rating-submit {
  border-radius: 13px;
}

@media (hover: hover) {
  .hub-tab:hover:not(.hub-tab--active) {
    border-color: rgba(var(--hub-primary-rgb), 0.14);
    background-color: rgba(var(--hub-primary-rgb), 0.055);
  }

  .trainer-appointment-card:hover,
  .rating-tag:hover {
    border-color: rgba(255, 255, 255, 0.16);
    background-color: rgba(255, 255, 255, 0.065);
  }

  .trainer-appointment-card:hover {
    transform: translateY(-2px);
  }

  .history-toggle:hover {
    border-color: rgba(var(--hub-primary-rgb), 0.24);
    background: rgba(var(--hub-primary-rgb), 0.06);
  }

  .type-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 48px rgba(0, 0, 0, 0.3);
  }
}

@media (max-width: 720px) {
  .selection-shell {
    padding: 46px 4px 54px;
  }

  .hub-header {
    margin-bottom: 26px;
  }

  .booking-options-section {
    margin-top: 36px;
    padding-top: 30px;
  }

  .bookings-overview {
    padding-top: 0;
  }

  .booking-card {
    grid-template-columns: auto minmax(0, 1fr);
    align-items: start;
  }

  .booking-card-action {
    grid-column: 2;
    justify-content: flex-start;
  }

  .appointments-notice {
    flex-wrap: wrap;
  }

  .appointments-notice button {
    width: 100%;
    margin-left: 32px;
    text-align: left;
  }

  .hub-tabs {
    gap: 5px;
    padding: 5px;
    border-radius: 19px;
  }

  .hub-tab {
    display: flex;
    min-height: 94px;
    flex-direction: column;
    gap: 5px;
    justify-content: center;
    padding: 10px 7px;
    border-radius: 15px;
    text-align: center;
  }

  .hub-tab-icon {
    width: 35px;
    height: 35px;
    border-radius: 11px;
  }

  .hub-tab-copy strong {
    font-size: 1rem;
  }

  .hub-tab-copy small {
    display: none;
  }

  .hub-tab-count {
    position: absolute;
    top: 8px;
    right: 8px;
    min-width: 22px;
    height: 22px;
    font-size: 0.75rem;
  }

  .next-appointment,
  .appointment-collection,
  .reservation-intro {
    border-radius: 22px;
  }

  .next-content {
    padding: 25px 0;
  }

  .date-tile {
    width: 76px;
    height: 90px;
    flex-basis: 76px;
  }

  .date-tile strong {
    font-size: 2rem;
  }

  .next-meta {
    grid-template-columns: 1fr 1fr;
  }

  .appointment-collection {
    padding: 18px;
  }

  .collection-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .scope-switch {
    width: 100%;
  }

  .scope-switch button {
    flex: 1;
  }

  .trainer-appointment-card {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 12px;
  }

  .appointment-card-title-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .appointment-card-title-row h3 {
    max-width: 100%;
  }

  .appointment-card-action {
    grid-column: 1 / -1;
    justify-content: flex-start;
    padding-left: 66px;
  }

  .rating-summary {
    align-items: flex-start;
  }

  .reservation-intro {
    align-items: flex-start;
    padding: 20px;
  }

  .reservation-intro-icon {
    width: 48px;
    height: 48px;
    flex-basis: 48px;
  }

  .reservations-list,
  .type-feed {
    grid-template-columns: 1fr;
  }

  .rating-card-content {
    padding: 30px 20px 22px !important;
  }

  .rating-actions {
    flex-direction: column-reverse;
  }

  .rating-actions .v-btn {
    width: 100%;
    margin: 0;
  }

  .state-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .state-card .v-btn {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 420px) {
  .booking-card-meta {
    flex-direction: column;
  }

  .next-meta {
    grid-template-columns: 1fr;
  }

  .next-content {
    align-items: flex-start;
  }

  .next-copy h2 {
    font-size: 1.45rem;
  }

  .appointment-card-facts {
    flex-direction: column;
    gap: 4px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hub-tab,
  .history-toggle,
  .history-toggle-chevron,
  .trainer-appointment-card,
  .type-card,
  .rating-tag,
  .card-action .v-icon {
    transition: none !important;
  }
}
</style>
